import { useEffect, useMemo } from 'react';

import type { InfiniteData } from '@tanstack/react-query';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import type {
  TBatchFindingsRequest,
  TBatchFindingsResponse,
} from '@/modules/claims/freeClaims/insuranceExport/types';

import { makeAPIRequestReactQuery } from '../api';
import { endpoints } from '../endpoints';
import type { GetAppointmentsResponse } from '../useAppointments';

// New batch-findings endpoint hook (for ClaimsPageRedesign)
export const useRcmTreatmentsBatch = (
  appointmentData?: InfiniteData<GetAppointmentsResponse>,
) => {
  const queryClient = useQueryClient();
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector(
    (state: {
      login: {
        user: {
          primaryPracticeId?: string;
          primaryOrganizationId?: string;
        };
      };
    }) => state.login.user,
  );

  // Transform data to appointments format for the batch-findings endpoint
  const flattenedAppointments =
    appointmentData?.pages?.flatMap((page) => page.data) || [];

  const appointments = flattenedAppointments
    .filter((appointment) => {
      return (
        appointment.appointment.unifiedId &&
        appointment.patient.imagingPatientId
      );
    })
    .map((appointment) => ({
      unifiedAppointmentId: Number(appointment.appointment.unifiedId),
      imagingPatientId: appointment.patient.imagingPatientId!,
    }));

  const groupedAppointments = useMemo(() => {
    const appointmentsLength = appointments.length;
    const groups: Array<TBatchFindingsRequest['appointments']> = [];
    for (let i = 0; i < appointmentsLength; i += 10) {
      const firstIndex = i;
      const lastIndex =
        i + 10 < appointmentsLength ? i + 10 : appointmentsLength;
      groups.push(appointments.slice(firstIndex, lastIndex));
    }
    return groups;
  }, [appointments]);

  const queries = useMemo(() => {
    return groupedAppointments.length
      ? groupedAppointments.map((appts, index) => {
          return {
            // Additional query keys:
            // - index: batch number (0, 1, 2, etc.) for stable ordering
            // - appts.length: number of appointments in this batch for cache differentiation
            // - appts[0]?.unifiedAppointmentId: first appointment ID for additional uniqueness
            queryKey: [
              endpoints.claimBatchFindings,
              index,
              appts.length,
              appts[0]?.unifiedAppointmentId,
            ],
            staleTime: 10 * 1000,
            queryFn: async () => {
              if (!appts?.length) {
                return [];
              }
              const resp = await makeAPIRequestReactQuery<
                TBatchFindingsResponse[]
              >(endpoints.claimBatchFindings, undefined, 'post', {
                headers: {
                  practice: currentPractice,
                  org: currentOrg,
                },
                data: {
                  appointments: appts,
                },
              });

              // Transform the response to match the expected TBatchTreatmentsResponse format
              // Check if resp is an array, if not, wrap it in an array
              const responseArray = Array.isArray(resp) ? resp : [resp];

              return responseArray.flatMap((batchResponse) => {
                // Return the appointmentData directly - no transformation needed
                return batchResponse.appointmentData.filter(Boolean);
              }) as TBatchFindingsResponse['appointmentData'];
            },
            enabled: Boolean(appts?.length && currentPractice && currentOrg),
          };
        })
      : [];
  }, [groupedAppointments, currentPractice, currentOrg]);

  // Clean up cache when component unmounts
  useEffect(() => {
    return () => {
      // Remove batch-findings queries from cache when component unmounts
      queryClient.removeQueries({
        queryKey: [endpoints.claimBatchFindings],
      });
    };
  }, [queryClient]);

  return useQueries({
    queries,
  });
};
